/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useState, useRef } from "react";
import Cookies from "js-cookie";

import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Box, TextField, IconButton, InputAdornment } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EmailIcon from "@mui/icons-material/Email";
import { Link, useNavigate, useLocation } from "react-router-dom";

import { capitalizeFirstLetter, capitalizeName } from "../../../utilities";
import SecondaryButton from "../../../components/shared/SecondaryButton";
import { getCurrentUserSelector } from "../../../redux/users/selector";
import { Roles } from "../../../utilities/constants";
import { setPreviousPath } from "../../../redux/utilities/actions";
import UserTable from "../../../components/shared/UserTable";
import PromoterFilter from "../../../components/shared/UserFilter";
import { filterFormFields, filterFormSchema } from "./components/filterDetails";
import PhoneNumberWithMenu from "../../../components/shared/PhoneNumberWithMenu";
import { getFilteredPromoters } from "../../../redux/promoter/actions";

const columns = (dispatch, location) => [
  {
    id: "userId",
    label: "ID",
    props: {
      align: "left",
    },
  },
  {
    id: "fullName",
    label: "Name",
    value: (row) => {
      const firstName = capitalizeName(row?.firstName) || "";
      const lastName = capitalizeName(row?.lastName) || "";
      return `${firstName} ${lastName}`.trim() || "-";
    },
    props: {
      align: "center",
    },
  },
  {
    id: "email",
    label: "E-mail",
    value: (row) => (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
        <Link
          to={`/promoters/${row.userId}`}
          style={{
            color: "inherit",
            textDecoration: "none",
            transition: "color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.color = "#d32f2f";
          }}
          onMouseLeave={(e) => {
            e.target.style.color = "inherit";
          }}
          onClick={() => {
            Cookies.remove("isFromDashboard");
            dispatch(setPreviousPath(`/promoters${location.search}`));
          }}>
          {row?.email}
        </Link>
        {row?.email && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `mailto:${row.email}`;
            }}
            sx={{
              padding: "4px",
              "&:hover": {
                color: "#d32f2f",
                backgroundColor: "rgba(211, 47, 47, 0.08)",
              },
            }}>
            <EmailIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    ),
    props: {
      align: "center",
    },
  },
  {
    id: "phone",
    label: "Mobile Number",
    value: (row) => <PhoneNumberWithMenu phone={row?.phone} isStart />,
    props: {
      align: "left",
    },
  },
  {
    id: "createdAt",
    label: "Registration Date",
    value: (row) => capitalizeFirstLetter(moment(row.createdAt).format("DD/MM/YYYY")),
    props: {
      align: "left",
    },
  },
];

const Promoters = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const lastPayloadRef = useRef(null);
  const currentPromoter = useSelector(getCurrentUserSelector);
  const filteredPromoters = useSelector((state) => state.promoter.filteredPromoters || []);
  const totalFiltered = useSelector((state) => state.promoter.totalFiltered || []);
  const [showFilters, setShowFilters] = useState(false);
  const [createdAtStartDate, setCreatedAtStartDate] = useState("");
  const [createdAtEndDate, setCreatedAtEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState(null);
  const [orderBy, setOrderBy] = useState(null);
  const [filters, setFilters] = useState([]);
  const [debouncedFilterPayload, setDebouncedFilterPayload] = useState([]);

  useEffect(() => {
    if (currentPromoter.role !== Roles.ADMIN.value) {
      navigate("/jobs");
    }
  }, [currentPromoter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterPayload(filters);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(0);
  }, [debouncedFilterPayload, createdAtStartDate, createdAtEndDate, debouncedSearch]);

  const applyFilterCookie = Cookies.get("applyFilter");

  let parsedCookieFilter = {};

  try {
    parsedCookieFilter = applyFilterCookie ? JSON.parse(applyFilterCookie) : {};
  } catch (e) {
    parsedCookieFilter = {};
  }

  const DEFAULT_FILTER_DATA = {
    page: 0,
    size: 10,
    registeredFrom: null,
    registeredTo: null,
    email: null,
    firstName: null,
    lastName: null,
    isAccountVerified: null,
    isEmailVerified: null,
    sortBy: "",
    sortDirection: "",
    generalSearch: null,
    phone: null,
    code: null,
    country: null,
    city: null,
  };

  const cleanObject = (obj) => {
    const res = {};
    Object.keys(obj || {}).forEach((key) => {
      if (obj[key] !== null && obj[key] !== undefined && obj[key] !== "") {
        res[key] = obj[key];
      }
    });
    return res;
  };

  useEffect(() => {
    const cleanedPayload = cleanObject({
      registeredFrom: createdAtStartDate,
      registeredTo: createdAtEndDate,
      generalSearch: debouncedSearch ? debouncedSearch.toLowerCase() : null,
      page,
      size: rowsPerPage,
      sortBy: orderBy,
      sortDirection: order,
      isAccountVerified: debouncedFilterPayload.isAccountVerified ? true : null,
      isEmailVerified: debouncedFilterPayload.isEmailVerified ? true : null,
      ...debouncedFilterPayload,
    });

    const filterData = {
      ...DEFAULT_FILTER_DATA,
      ...cleanedPayload,
      isAccountVerified: cleanedPayload.isAccountVerified ? true : null,
      isEmailVerified: cleanedPayload.isEmailVerified ? true : null,
    };

    const payloadString = JSON.stringify(filterData);
    if (lastPayloadRef.current === payloadString) return;
    lastPayloadRef.current = payloadString;

    dispatch(getFilteredPromoters({ ...filterData, ...parsedCookieFilter }));
  }, [createdAtStartDate, createdAtEndDate, debouncedSearch, page, rowsPerPage, order, orderBy, debouncedFilterPayload]);

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {/* Single row with date filters and buttons - aligned with table */}
      <Box
        sx={{
          width: "95%",
          margin: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: "10px",
          marginBottom: "10px",
        }}>
        {/* Date filters on the left */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: "10px",
            width: { xs: "100%", md: "auto" },
          }}>
          <TextField
            label="Registered From"
            type="date"
            size="small"
            value={createdAtStartDate}
            onChange={(e) => setCreatedAtStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: "180px" } }}
          />
          <TextField
            label="Registered To"
            type="date"
            size="small"
            value={createdAtEndDate}
            onChange={(e) => setCreatedAtEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: "180px" } }}
          />
          <IconButton
            onClick={() => {
              setCreatedAtStartDate("");
              setCreatedAtEndDate("");
              setSearchTerm("");
            }}
            disabled={!createdAtStartDate && !createdAtEndDate && !searchTerm}
            sx={{
              borderRadius: "4px",
              alignSelf: { xs: "center", sm: "auto" },
              "&:hover": {
                borderBottom: "2px solid #C02026",
                backgroundColor: "transparent",
              },
            }}>
            <ClearIcon sx={{ color: "#C02026" }} />
          </IconButton>
          <TextField
            label="General search"
            type="text"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, city..."
            sx={{ width: { xs: "100%", sm: "280px" } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: "#C02026" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Buttons on the right */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: "10px",
            width: { xs: "100%", md: "auto" },
          }}>
          <SecondaryButton onClick={toggleFilters} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {showFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </SecondaryButton>
        </Box>
      </Box>

      <Box sx={{ display: showFilters ? "block" : "none" }}>
        {filteredPromoters && (
          <PromoterFilter
            filterSteps={filterFormFields()}
            filterSchema={filterFormSchema}
            onFilterChange={(updatedFilters) => {
              setFilters(updatedFilters);
            }}
            shouldStartOpen
            hideInternalButton
          />
        )}
      </Box>
      <UserTable
        columns={columns(dispatch, location)}
        data={filteredPromoters || []}
        isLoading={false}
        order={order}
        orderBy={orderBy}
        onSort={(e, property) => {
          setOrder(orderBy === property && order === "asc" ? "desc" : "asc");
          setOrderBy(property);
        }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        noResultsContent="There are no promoters."
        totalData={totalFiltered}
      />
    </Box>
  );
};

export default Promoters;
